import { ReactElement, useEffect } from "react";
import { FormField, RadioButtonGroup } from "@justworkshr/milo-form";
import MessageWithLink from "pages/employer-of-record/components/MessageWithLink/MessageWithLink";
import styles from "../../../../SetupFormGenerator.module.css";
import { RadioButtonCard } from "pages/employer-of-record/payments/components/RadioButtonCard";
import { GeneratedComponentPropWithSet, Option } from "../../../../types";
import { FormPayloadType } from "../../../../../../types";

type RadioLayout = "horizontal" | "vertical";

interface RadioInputProps<T extends FormPayloadType = FormPayloadType>
  extends GeneratedComponentPropWithSet<T> {
  layout?: RadioLayout;
}

export function RadioInput<T extends FormPayloadType = FormPayloadType>({
  field,
  accessor,
  value,
  setFieldValue,
  errorField,
  touchedField,
  layout = "horizontal", // Default to horizontal for backward compatibility
}: RadioInputProps<T>): ReactElement {
  // Determine the CSS class based on layout prop
  const layoutClass =
    layout === "vertical"
      ? styles.verticalRadioButtonWrapper
      : styles.horizontalRadioButtonWrapper;

  // Set default option on first render if one exists and no value is selected
  useEffect(() => {
    if (!value && field.options) {
      const defaultOption = field.options.find(
        (option: Option) => option.default
      );
      if (defaultOption) {
        setFieldValue(`setUp.${accessor}`, defaultOption.value);
      }
    }
  }, [value, field.options, accessor, setFieldValue]);

  return (
    <div className={`${errorField && touchedField ? styles.error : ""}`}>
      <FormField
        label={field.label}
        required={field.required}
        error={errorField && touchedField ? errorField : ""}
      >
        <>
          {field.message && (
            <div className={styles.description}>
              <MessageWithLink {...field.message} />
            </div>
          )}
          <RadioButtonGroup name={field.name}>
            <div className={layoutClass} data-testid="radio-wrapper">
              {field.options?.map((option: Option) => (
                <RadioButtonCard
                  key={option.value}
                  name={field.name}
                  label={option.label}
                  description={option.description || ""}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(value) =>
                    setFieldValue(`setUp.${accessor}`, value)
                  }
                />
              ))}
            </div>
          </RadioButtonGroup>
        </>
      </FormField>
    </div>
  );
}
